import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, RefObject, useCallback, useEffect } from 'react';
import {
	ChatAttributeType,
	ChatCollectionAttribute,
	ChatNumberAttribute,
	ChatPhoneAttribute,
	ChatQuestionaryField,
	ChatRatingAttribute,
	ChatSelectAttribute,
	ChatStringAttribute,
	ChatVariantsAttribute,
	FieldDescriptor,
} from 'sdk';

import { useSDK } from '../../../app';
import { Field } from '../fields/field';
import { MailIcon } from '../fields/icons/mailIcon';
import { PhoneIcon } from '../fields/icons/phoneIcon';
import { getFormValues } from '../utils/getFormValues';

const iconsMap = {
	PHONE: <PhoneIcon />,
	EMAIL: <MailIcon />,
} as { [k: string]: ReactNode };

type FieldDescriptorValuesType<T = unknown> = T extends FieldDescriptor<infer X> ? X : unknown;

type Props = {
	errors: { [fieldName: string]: string };
	values: readonly FieldDescriptorValuesType<FieldDescriptor>[];
	formRef: RefObject<HTMLFormElement>;
};

export const Form = observer<Props>(
	({ values, errors, formRef }): ReactElement => {
		const { formsStore } = useSDK();
		useEffect(() => {
			const form = formRef.current;

			if (form) {
				return (): void => {
					const values = getFormValues(form);
					values.forEach((v) => {
						v.value !== undefined && formsStore.setCachedValue(v.key, v.value);
					});
				};
			}
		}, [formRef, formsStore]);

		return (
			<form onSubmit={useCallback((e): void => e.preventDefault(), [])} ref={formRef}>
				{values.map((field) => {
					const attribute = field.attribute;
					const cached = formsStore.getCachedValue(attribute.key);
					const initialValue =
						cached ||
						(
							attribute as
								| ChatNumberAttribute
								| ChatStringAttribute
								| ChatSelectAttribute
								| ChatCollectionAttribute
						).valueObject;
					return (
						<Field
							key={field.settings.id}
							usePhoneMask={(attribute as ChatPhoneAttribute).usePhoneMask}
							error={errors[attribute.key]}
							icon={iconsMap[attribute.type] || null}
							name={attribute.key}
							fieldName={field.settings.title || attribute.title}
							required={field.settings.isRequired}
							initialValue={initialValue || (field as ChatQuestionaryField).settings.defaultValue}
							type={attribute.type}
							attributes={
								attribute.type === ChatAttributeType.Rating
									? [...Array((attribute as ChatRatingAttribute).gradations + 1).keys()].map((i) => {
											return { key: i.toString(), title: i.toString() };
									  })
									: (attribute as ChatVariantsAttribute).availableValues
							}
						/>
					);
				})}
			</form>
		);
	},
	{ forwardRef: true }
);
